import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFloppyDisk,
  faPlus,
  faSpinner,
  faTrash,
  faUserTie,
} from '@fortawesome/free-solid-svg-icons'
import { hrToolsService } from '../../services/hrToolsService'
import { MAX_POSITION_LENGTH, MAX_POSITIONS } from '../../utils/positions'

const HrPositionsCard: React.FC = () => {
  const [draft, setDraft] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const next = await hrToolsService.getPositions()
        if (!cancelled) setDraft(next)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load positions')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const patchAt = (index: number, value: string) => {
    setDraft((prev) => prev.map((item, i) => (i === index ? value : item)))
    setMessage(null)
  }

  const removeAt = (index: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== index))
    setMessage(null)
  }

  const addRow = () => {
    setDraft((prev) => (prev.length >= MAX_POSITIONS ? prev : [...prev, '']))
    setMessage(null)
  }

  const onSave = async () => {
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const next = await hrToolsService.savePositions(draft)
      setDraft(next)
      setMessage('Saved.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title">
          <FontAwesomeIcon icon={faUserTie} />
          Positions
        </h2>
        <p className="text-sm text-base-content/70">
          These names are the position choices when HR or an admin sets someone&apos;s position.
          Daily form reminders go to the position selected on that notification. Renaming a
          position here does not rewrite people who already have the old name.
        </p>

        {error ? <div className="alert alert-error mt-2"><span>{error}</span></div> : null}
        {message ? <div className="alert alert-success mt-2"><span>{message}</span></div> : null}

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-base-content/70">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Loading positions…
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {draft.length === 0 ? (
              <p className="text-sm text-base-content/60">No positions yet. Add one, such as Foreman or Technician.</p>
            ) : (
              <div className="space-y-2">
                {draft.map((value, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      className="input input-bordered w-full"
                      value={value}
                      maxLength={MAX_POSITION_LENGTH}
                      onChange={(e) => patchAt(index, e.target.value)}
                      placeholder="Position name"
                      aria-label={`Position ${index + 1}`}
                    />
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm btn-square text-error"
                      onClick={() => removeAt(index)}
                      aria-label="Remove"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn btn-outline"
                disabled={draft.length >= MAX_POSITIONS}
                onClick={addRow}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add position
              </button>
              <button
                type="button"
                className="btn btn-primary gap-2"
                disabled={saving}
                onClick={() => void onSave()}
              >
                <FontAwesomeIcon
                  icon={saving ? faSpinner : faFloppyDisk}
                  className={saving ? 'animate-spin' : ''}
                />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HrPositionsCard
